import type { Widget, WidgetParameter } from "../types/widget";
import {
  applyDraftOutputEdits,
  resolveEditableDraftOutput,
  type DraftOutputFormat,
} from "./widgetDraftApproval";

export const DEFAULT_WIDGET_PARAMETERS: WidgetParameter[] = [
  { name: "from", type: "datetime", bind: "dashboard.dateFrom", required: true },
  { name: "to", type: "datetime", bind: "dashboard.dateTo", required: true },
];

export type EditableDraftFormState = {
  description: string;
  indicesText: string;
  timeField: string;
  dslText: string;
  mappingHintText: string;
  parameters: WidgetParameter[];
  format: DraftOutputFormat;
  mapping: Record<string, unknown>;
};

export function emptyEditableDraftForm(): EditableDraftFormState {
  return resolveEditableDraftForm({ dataSource: { indices: [], dslTemplate: {} } });
}

export function dslToEditorText(dsl: unknown): string {
  if (dsl == null) return "{}";
  try {
    return JSON.stringify(dsl, null, 2);
  } catch {
    return "{}";
  }
}

export function resolveEditableDraftForm(widget: Widget): EditableDraftFormState {
  const output = resolveEditableDraftOutput(widget);
  const parameters = widget.dataSource?.parameters?.length
    ? widget.dataSource.parameters.map((p) => ({ ...p }))
    : DEFAULT_WIDGET_PARAMETERS.map((p) => ({ ...p }));

  return {
    description: widget.description || "",
    indicesText: (widget.dataSource?.indices || []).join(", "),
    timeField: widget.dataSource?.timeField || "",
    dslText: dslToEditorText(widget.dataSource?.dslTemplate),
    mappingHintText: dslToEditorText(widget.dataSource?.mappingHint ?? {}),
    parameters,
    format: output.format,
    mapping: output.mapping,
  };
}

function parseJsonObjectField(raw: string, label: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error(`${label} must be a JSON object`);
    }
    return parsed as Record<string, unknown>;
  } catch (e: unknown) {
    if (e instanceof Error && e.message.includes("must be")) throw e;
    throw new Error(`${label}: invalid JSON`);
  }
}

export function applyDraftFormEdits(widget: Widget, form: EditableDraftFormState): Widget {
  const indices = form.indicesText
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const dslTemplate = parseJsonObjectField(form.dslText, "DSL");
  const mappingHint = parseJsonObjectField(form.mappingHintText, "Mapping hint");

  const withOutput = applyDraftOutputEdits(widget, form.format, form.mapping);
  return {
    ...withOutput,
    description: form.description.trim() || undefined,
    dataSource: {
      ...withOutput.dataSource,
      indices,
      timeField: form.timeField.trim() || withOutput.dataSource?.timeField,
      dslTemplate,
      parameters: form.parameters,
      mappingHint: Object.keys(mappingHint).length > 0 ? mappingHint : undefined,
    },
  };
}

export function isValidJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}
